import '@sendbird/uikit-react/dist/index.css';
import { lazy, Suspense, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import ConnectInjector from '@/components/common/ConnectInjector';
import NotificationInjector from '@/components/common/NotificationInjector';
import CustomConnectionHandler from '@/components/handler/CustomConnectionHandler';
import CustomGroupChannelHandler from '@/components/handler/CustomGroupChannelHandler';
import ModalPortal from '@/components/modal/ModalPortal';
import useModalHook from '@/hooks/useModalHook';
import useAuthStore from '@/stores/authStore';
import useControlOpenPanels from '@/stores/controlOpenPanelsStore';
import useCurrentChannel from '@/stores/currentChannelStore';
import useMessageStore from '@/stores/messageStore';

const ChannelListView = lazy(() => import('@/views/channelList/ChannelListView'));
const ChannelView = lazy(() => import('@/views/channel/ChannelView'));
const ChannelSettingsView = lazy(() => import('@/views/channelSettings/ChannelSettingsView'));

const ChatMainPage = () => {
  const { userInfo } = useAuthStore();
  const { currentChannel, setCurrentChannel, clearCurrentChannel } = useCurrentChannel();
  const { updateMessage } = useMessageStore();
  const { openModal } = useModalHook();
  const [searchParams, setSearchParams] = useSearchParams();

  const { isOpenChannelSetting, openPanel, changeOpenChannelSettings } = useControlOpenPanels();

  const [message, setMessage] = useState<string>('');

  const [activeChannelUrl, setActiveChannelUrl] = useState('');

  const stringifiedCurrentChannel = JSON.stringify(currentChannel);

  useEffect(() => {
    if (!userInfo) {
      return;
    }
    // NOTICE: Search 입력 값에 매칭되는 channel이 없을 때 채팅 UI에 '새로운 메세지...'로 보여져야 한다면 activeChannel 빈 string 넣으면 됨.
    setActiveChannelUrl(currentChannel?.url || '');
    setMessage('');
  }, [stringifiedCurrentChannel, currentChannel, userInfo]);

  useEffect(() => {
    return () => {
      clearCurrentChannel();
    };
  }, [clearCurrentChannel]);

  // 대시보드에서 open=setting-modal로 진입한 경우, 채팅 UI(activeChannelUrl)가 준비된 뒤 설정 모달을 1회 연다.
  // 오픈 후 open 파라미터를 제거 → 새로고침/재진입 시 재오픈되지 않는다.
  useEffect(() => {
    if (!!activeChannelUrl && searchParams.get('open') === 'setting-modal') {
      openModal({ type: 'settingModal', props: {} });

      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('open');
      setSearchParams(nextParams, { replace: true });
    }
  }, [activeChannelUrl, searchParams, setSearchParams, openModal]);

  if (!userInfo) {
    return <div></div>;
  }

  // appId를 env에서도 관리 중. decrypt해서 사용하는게 나은지, env에서 관리하는게 나은지 논의.
  // const _appId = CryptoJS.AES.decrypt(appId!, clientId!).toString(CryptoJS.enc.Utf8);

  // MARK: fallback
  return (
    <Suspense fallback={<div></div>}>
      <>
        {/* 왼쪽 영역 - 헤더, 검색, 리스트 */}
        <ChannelListView
          activeChannelUrl={activeChannelUrl || ''}
          onChannelSelect={(channel) => {
            if (message.length > 0) {
              updateMessage({
                message,
                url: activeChannelUrl,
              });
            }
            setCurrentChannel(channel);
          }}
        />

        {/* 오른쪽 영역 - 메세지 */}
        <ChannelView
          changeMessages={{ message, setMessage }}
          activeChannelUrl={activeChannelUrl || ''}
          onChatHeaderAction={() => {
            openPanel('setting');
          }}
        />
        {/* 채널 정보 */}
        {Boolean(activeChannelUrl) && (
          <ChannelSettingsView
            channelUrl={activeChannelUrl || ''}
            isOpen={isOpenChannelSetting}
            onCloseClick={() => {
              changeOpenChannelSettings(false);
            }}
          />
        )}

        <ConnectInjector />
        <NotificationInjector />
        <ModalPortal />
      </>
      <CustomConnectionHandler />
      <CustomGroupChannelHandler />
    </Suspense>
  );
};

export default ChatMainPage;
